import { AutoAwesomeOutlined, Close } from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { getCommandKey } from '@/utilities/platform';
import {
  IconButton,
  InputAdornment,
  InputBase,
  Paper,
  alpha,
  styled,
} from '@mui/material';
import { CSSProperties, ChangeEvent, KeyboardEvent, RefObject } from 'react';

const SearchBox = styled(Paper)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.75),
  '&:hover': {
    backgroundColor: theme.palette.common.white,
  },
  width: '100%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(1)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: theme.typography.body2.fontSize,
    [theme.breakpoints.up('lg')]: {
      width: '20ch',
    },
  },
}));

/**
 * Props for the SearchInput component
 */
interface SearchInputProps {
  /** Current value of the search input */
  value: string;
  /** Called when input value changes */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Called when a key is pressed */
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Called when the close button is clicked */
  onClose: () => void;
  /** Whether the search is currently open */
  isOpen: boolean;
  /** Reference to the input element */
  inputRef: RefObject<HTMLInputElement>;
  /** Additional styles for the container */
  sx?: CSSProperties;
}

/**
 * A search input component with a search icon, input field, and close button.
 * Used as the main input for the global search functionality.
 */
export function GlobalSearchInput({
  value,
  onChange,
  onKeyDown,
  onClose,
  isOpen,
  inputRef,
  sx = {},
}: SearchInputProps) {
  return (
    <SearchBox sx={{ margin: 0, width: '100%', ...sx }}>
      <SearchIconWrapper>
        <AutoAwesomeOutlined
          fontSize="small"
          sx={{
            fontSize: 14,
            color: 'primary.main',
          }}
        />
      </SearchIconWrapper>
      <StyledInputBase
        placeholder={`Search with AI (${getCommandKey()}K)`}
        inputRef={inputRef}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
        sx={{
          width: '100%',
          paddingLeft: 1,
          paddingRight: 2,
        }}
        endAdornment={
          // Always render InputAdornment to reserve space for the X
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              onClick={onClose}
              aria-label="close search"
              sx={{ padding: 0 }}
            >
              {isOpen ? (
                <Close fontSize="small" />
              ) : (
                // Transparent icon to reserve space
                <Close
                  fontSize="small"
                  sx={{ opacity: 0, pointerEvents: 'none' }}
                />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
    </SearchBox>
  );
}
